import { RESULT_ITEM_TYPES, localizedCopy } from 'features/result-content/resultContent';

export const ALL_FILTER = 'all';
export const CATALOG_PAGE_SIZE = 100;
export const CATALOG_MAX_PAGES = 50;

export const RESULT_ITEM_TYPE_ALIASES = {
  key_strength: 'key_strength',
  growth_opportunity: 'professional_growth_challenge_opportunity',
  professional_growth_challenge_opportunity: 'professional_growth_challenge_opportunity',
  career_path: 'suggested_career_path',
  suggested_career_path: 'suggested_career_path',
  skill: 'skill_worth_developing',
  skills: 'skill_worth_developing',
  skill_worth_developing: 'skill_worth_developing',
};

const PERSONAL_SUMMARY_TYPES = new Set([
  'personal_summary',
  'personal_summaries',
  'summary',
  'summaries',
]);

const EXCLUDED_GROUP_KEYS = new Set([
  'meta',
  'links',
  'tags',
  'personal_summary',
  'personal_summaries',
  'summaries',
  'success',
  'message',
  'revision',
]);

export function canonicalResultItemType(type) {
  if (type == null || type === '') {
    return '';
  }
  const slug = String(type).trim();
  return RESULT_ITEM_TYPE_ALIASES[slug] || slug;
}

export function isRepeatingResultItemType(type) {
  const canonical = canonicalResultItemType(type);
  return RESULT_ITEM_TYPES.includes(canonical);
}

export function isAllFilter(value) {
  return value == null || value === '' || value === ALL_FILTER;
}

function readNumericId(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

function asTagList(raw) {
  if (Array.isArray(raw?.tags) && raw.tags.length) {
    return raw.tags.filter(Boolean);
  }
  if (raw?.tag) {
    return [raw.tag];
  }
  return [];
}

function tagIdentifiers(tag) {
  if (tag == null) {
    return [];
  }
  if (typeof tag !== 'object') {
    return [String(tag)];
  }
  return [
    tag.id,
    tag.tag_id,
    tag.key,
    tag.slug,
    tag.import_key,
    tag.result_item_key,
  ]
    .filter((value) => value != null && value !== '')
    .map((value) => String(value).toLowerCase());
}

export function catalogItemKey(raw) {
  if (!raw || typeof raw !== 'object') {
    return '';
  }
  const key = raw.result_item_key || raw.import_key || raw.key;
  if (key != null && String(key).trim() !== '') {
    return String(key).trim();
  }
  const id = readNumericId(raw.id ?? raw.result_item_id);
  return id != null ? String(id) : '';
}

export function normalizeResultItem(raw) {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const sourceType = raw.type || raw.relationship_type || raw.result_item_type;
  const type = canonicalResultItemType(sourceType);
  if (!isRepeatingResultItemType(type) || PERSONAL_SUMMARY_TYPES.has(String(sourceType || '').trim())) {
    return null;
  }
  const id = readNumericId(raw.id ?? raw.result_item_id);
  const resultItemKey = catalogItemKey(raw);
  if (!resultItemKey && id == null) {
    return null;
  }
  const tags = asTagList(raw);
  const titleAr = String(raw.title_ar || raw.title || '').trim();
  const titleEn = String(raw.title_en || raw.title || '').trim();
  return {
    id: id ?? resultItemKey,
    result_item_id: id,
    result_item_key: resultItemKey || String(id),
    type: sourceType || type,
    canonical_type: type,
    source_type: sourceType || type,
    title_ar: titleAr,
    title_en: titleEn,
    description_ar: String(raw.description_ar || raw.description || ''),
    description_en: String(raw.description_en || raw.description || ''),
    advice_ar: String(raw.advice_ar || raw.advice || ''),
    advice_en: String(raw.advice_en || raw.advice || ''),
    tags,
    tag: raw.tag || tags[0] || null,
    tag_id: raw.tag_id ?? raw.tag?.id ?? tags[0]?.id ?? null,
    is_active: raw.is_active !== false && String(raw.status || '').toLowerCase() !== 'inactive',
  };
}

function flattenGroupedCollections(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return [];
  }
  const rows = [];
  Object.entries(payload).forEach(([key, value]) => {
    if (EXCLUDED_GROUP_KEYS.has(key) || PERSONAL_SUMMARY_TYPES.has(key)) {
      return;
    }
    if (Array.isArray(value)) {
      rows.push(...value);
    }
  });
  return rows;
}

export function extractCatalogRows(payload) {
  if (payload == null) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (typeof payload !== 'object') {
    return [];
  }
  if (Array.isArray(payload.items)) {
    return payload.items;
  }
  if (payload.items && typeof payload.items === 'object') {
    const groupedItems = flattenGroupedCollections(payload.items);
    if (groupedItems.length) {
      return groupedItems;
    }
  }
  if (Array.isArray(payload.data)) {
    return payload.data;
  }
  if (payload.data && typeof payload.data === 'object') {
    const nested = extractCatalogRows(payload.data);
    if (nested.length) {
      return nested;
    }
  }
  if (Array.isArray(payload.result_items)) {
    return payload.result_items;
  }
  return flattenGroupedCollections(payload);
}

export function readListMeta(payload, items = []) {
  const meta = payload && typeof payload === 'object' && !Array.isArray(payload)
    ? (payload.meta && typeof payload.meta === 'object' ? payload.meta : payload)
    : {};
  const total = Number(meta.total);
  const perPage = Number(meta.per_page || meta.perPage);
  const current = Number(meta.current_page || meta.currentPage);
  const last = Number(meta.last_page || meta.lastPage);
  return {
    current_page: Number.isFinite(current) && current > 0 ? current : 1,
    last_page: Number.isFinite(last) && last > 0 ? last : 1,
    per_page: Number.isFinite(perPage) && perPage > 0 ? perPage : items.length,
    total: Number.isFinite(total) && total >= 0 ? total : items.length,
  };
}

export function uniqueCatalogItems(items = []) {
  const seen = new Set();
  return (items || []).filter((item) => {
    const key = item?.result_item_key;
    if (!key || key === ALL_FILTER || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function normalizeResultItemCatalog(payload) {
  const rows = extractCatalogRows(payload)
    .map(normalizeResultItem)
    .filter(Boolean)
    .filter((item) => item.result_item_key !== ALL_FILTER);
  const items = uniqueCatalogItems(rows);
  return {
    items,
    meta: readListMeta(payload, items),
  };
}

export function itemMatchesSearch(item, search) {
  const term = String(search || '').trim().toLowerCase();
  if (!term) {
    return true;
  }
  const haystack = [
    item.title_ar,
    item.title_en,
    item.description_ar,
    item.description_en,
    item.advice_ar,
    item.advice_en,
  ].join(' ').toLowerCase();
  return haystack.includes(term);
}

export function itemMatchesType(item, typeFilter) {
  if (isAllFilter(typeFilter)) {
    return isRepeatingResultItemType(item.type);
  }
  return canonicalResultItemType(item.canonical_type || item.type) === canonicalResultItemType(typeFilter);
}

function selectedTagRecord(tagFilter, tagFilterOptions = []) {
  if (isAllFilter(tagFilter)) {
    return null;
  }
  const needle = String(tagFilter).toLowerCase();
  return (tagFilterOptions || []).find((tag) => tagIdentifiers(tag).includes(needle)) || null;
}

export function itemMatchesTag(item, tagFilter, tagFilterOptions = []) {
  if (isAllFilter(tagFilter)) {
    return true;
  }
  const needle = String(tagFilter).toLowerCase();
  const selected = selectedTagRecord(tagFilter, tagFilterOptions);
  const allowed = new Set([
    needle,
    ...tagIdentifiers(selected),
  ].filter(Boolean));
  const tags = asTagList(item);
  if (tags.some((tag) => tagIdentifiers(tag).some((id) => allowed.has(id)))) {
    return true;
  }
  if (item.tag_id != null && allowed.has(String(item.tag_id).toLowerCase())) {
    return true;
  }
  return false;
}

export function filterCatalogItems(items, { typeFilter, tagFilter, search, tagFilterOptions } = {}) {
  return (items || []).filter((item) => (
    itemMatchesType(item, typeFilter)
    && itemMatchesTag(item, tagFilter, tagFilterOptions)
    && itemMatchesSearch(item, search)
    && item.is_active !== false
  ));
}

export function localizedItemTitle(item, locale) {
  return localizedCopy(item, locale, 'title');
}

export function localizedItemDescription(item, locale) {
  return localizedCopy(item, locale, 'description');
}

export function typeFilterOptions(t) {
  return [
    { value: ALL_FILTER, label: t('filters.all') },
    ...RESULT_ITEM_TYPES.map((type) => ({
      value: type,
      label: t(`resultItems.types.${type}`),
    })),
  ];
}

export function tagFilterOptionsFromTags(tags, locale, t) {
  return [
    { value: ALL_FILTER, label: t('filters.all') },
    ...(tags || []).map((tag) => ({
      value: String(tag.id ?? tag.slug ?? tag.key),
      label: localizedCopy(tag, locale, 'name') || tag.slug || String(tag.id),
      tag,
    })),
  ];
}

export async function fetchResultItemCatalog(listResultItems, filters = {}, config) {
  const pageSize = Number(filters.per_page) > 0 ? Number(filters.per_page) : CATALOG_PAGE_SIZE;
  const requestFilters = {
    is_active: filters.is_active ?? true,
    per_page: pageSize,
  };
  const firstPayload = await listResultItems({ ...requestFilters, page: 1 }, config);
  const first = normalizeResultItemCatalog(firstPayload);
  const lastPage = Math.min(Number(first.meta.last_page) || 1, CATALOG_MAX_PAGES);
  if (lastPage <= 1) {
    return first;
  }
  const pages = [first];
  for (let page = 2; page <= lastPage; page += 1) {
    const payload = await listResultItems({ ...requestFilters, page, per_page: pageSize }, config);
    pages.push(normalizeResultItemCatalog(payload));
  }
  const items = uniqueCatalogItems(pages.flatMap((page) => page.items));
  return {
    items,
    meta: {
      current_page: 1,
      last_page: 1,
      per_page: items.length,
      total: Number(first.meta.total) > 0 ? Number(first.meta.total) : items.length,
    },
  };
}
